import { OidcConfig, ProgramUser } from 'features/auth/types';
import {
  LS_IS_CAREGIVER_WAITING_FOR_APPROVAL,
  LS_ITEM_ACCESS_TOKEN,
  LS_ITEM_IS_LOGGED_IN,
  LS_ITEM_OIDC_TOKEN,
  LS_ITEM_REFRESH_TOKEN,
} from 'store/constants';

interface AuthState {
  accessToken: string | null;
  refreshToken: string | null;
  user: ProgramUser | null;
  hospitalLogoUrl: string | undefined;
  oidcConfig: OidcConfig | null;
  isLoggedIn: boolean;
  isCaregiverPendingApproval: boolean;
  isUserDoneIneligible: boolean;
}

/**
 * We define `getInitialAuthState` to avoid stale data issues with localStorage values (e.g. accessToken, refreshToken).
 * When the slice resets to `initialState` a static object that reads
 * from localStorage just once wont reflect the latest values.
 * By using a function, we pulls the latest values always
 */

/**
 * Retrieves the initial authentication state from local storage.
 *
 * @returns An object containing authentication state:
 *  - accessToken: The user's access token from local storage
 *  - refreshToken: The user's refresh token from local storage
 *  - user: Initially null, for user data
 *  - hospitalId: Initially null, for hospital identification
 *  - oidcConfig: OIDC configuration object from local storage if exists, null otherwise
 */
export const getInitialAuthState = (): AuthState => ({
  accessToken: localStorage.getItem(LS_ITEM_ACCESS_TOKEN),
  refreshToken: localStorage.getItem(LS_ITEM_REFRESH_TOKEN),
  user: null,
  hospitalLogoUrl: undefined,
  oidcConfig: localStorage.getItem(LS_ITEM_OIDC_TOKEN)
    ? (JSON.parse(localStorage.getItem(LS_ITEM_OIDC_TOKEN)!) as OidcConfig)
    : null,
  isLoggedIn: localStorage.getItem(LS_ITEM_IS_LOGGED_IN) === 'true',
  isCaregiverPendingApproval: localStorage.getItem(LS_IS_CAREGIVER_WAITING_FOR_APPROVAL) === 'true',
  isUserDoneIneligible: false,
});

export const updateIsLoggedInLocalStorage = (isLoggedIn: boolean) => {
  localStorage.setItem(LS_ITEM_IS_LOGGED_IN, isLoggedIn.toString());
};
